import React, { useEffect, useRef, useState } from "react";
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButton,
    IonItem,
    IonLabel,
    IonModal,
} from "@ionic/react";
import { Helmet } from "react-helmet";

const wordsList = [
    "elephant", "guitar", "umbrella", "computer", "banana", "keyboard", "aeroplane", "butterfly", "mountain", "calendar",
    "notebook", "backpack", "television", "sandwich", "rainbow", "flashlight", "lighthouse", "adventure", "watermelon", "microscope",
    "chocolate", "telephone", "refrigerator", "basketball", "binoculars", "chandelier", "fireworks", "kangaroo", "scarecrow", "pineapple",
    "dragonfly", "sunflower", "waterfall", "strawberry", "parachute", "helicopter", "saxophone", "marathon", "telescope", "firefighter",
    "skyscraper", "bicycle", "nightingale", "laboratory", "spaceship", "hamburger", "whistle", "chessboard", "calculator", "iceberg",
    "playground", "suitcase", "fireplace", "windmill", "snowflake", "popcorn", "typewriter", "volleyball", "tornado", "treasure",
    "racehorse", "hedgehog", "octopus", "watercolour", "trampoline", "carousel", "penguin", "magnifying", "cheeseburger", "firecracker",
    "jellyfish", "laptop", "raincoat", "unicorn", "microwave", "goldfish", "buttercup", "stethoscope", "mooncake", "caterpillar",
    "xylophone", "thermometer", "dinosaur", "lighthouse", "watermelon", "skateboard", "sunblock", "parrot", "snowman", "firefly",
    "volcano", "ladder", "rainforest", "seashell", "firewood", "blueberry", "dragon", "castle", "notepad", "astronaut",
    "compass", "newspaper", "birthday", "jigsaw", "paintbrush", "zeppelin", "rhinoceros", "windchime", "avocado", "carousel",
    "coconut", "woodpecker", "hurricane", "boomerang", "porcupine", "satellite", "ladybug", "treetop", "starfish", "compass",
    "fountain", "beachball", "snowboard", "treasure", "cupcake", "firetruck", "flashlight", "lantern", "popsicle", "puzzle",
    "banjo", "watergun", "honeycomb", "teapot", "yacht", "mermaid", "scuba", "lantern", "surfboard", "zebra",
    "icicle", "lollipop", "hammock", "sprinkler", "snowstorm", "marbles", "dolphin", "grasshopper", "crayons", "balloon",
    "microscope", "boombox", "accordion", "teddybear", "ferriswheel", "hiking", "earthquake", "peacock", "lighthouse", "sundial",
    "mailbox", "waterpark", "rocketship", "parachute", "tugboat", "seagull", "doghouse", "quicksand", "meadow", "snowglobe",
    "firepit", "chameleon", "shoelace", "yo-yo", "woodland", "jukebox", "hotairballoon", "bubblegum", "cliffside", "firealarm",
    "paddleboat", "goldenretriever", "carouselhorse", "popsicle", "lightbulb", "jellybean", "whirlpool", "cuckooclock", "sunglasses", "rainboots",
    "ladybug", "chesspiece", "cornfield", "wheelbarrow", "harmonica", "tangerine", "hologram", "fireworks", "schoolbus", "globe"
];

const VowelConsonant: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [questionType, setQuestionType] = useState<"vowel" | "consonant">("vowel");
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);


    const modal = useRef<HTMLIonModalElement>(null);

    // Utility to shuffle arrays
    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    // Count vowels and consonants
    const getVowelConsonantCount = (word: string) => {
        const vowels = "aeiouAEIOU";
        let vowelCount = 0, consonantCount = 0;

        for (const char of word) {
            if (vowels.includes(char)) {
                vowelCount++;
            } else if (char.match(/[a-zA-Z]/)) {
                consonantCount++;
            }
        }

        return { vowelCount, consonantCount };
    };

    // Generate 4 options including correct
    const generateOptions = (correct: number): number[] => {
        const optionSet = new Set<number>();
        optionSet.add(correct);

        while (optionSet.size < 4) {
            const variation = Math.floor(Math.random() * 3) + 1;
            const candidate = Math.random() > 0.5 ? correct + variation : correct - variation;
            if (candidate >= 0) optionSet.add(candidate);
        }

        return shuffleArray(Array.from(optionSet));
    };

    // On option click
    const handleOptionClick = (selectedOption: number) => {
        const word = wordsList[currentIndex];
        const { vowelCount, consonantCount } = getVowelConsonantCount(word);
        const correctAnswer = questionType === "vowel" ? vowelCount : consonantCount;

        if (selectedOption === correctAnswer) {
            setShowImage(true);
            setMessage("✅ Correct!");
            setTimeout(() => {
                const nextIndex = currentIndex !== wordsList.length - 1 ? currentIndex + 1 : 0;
                const nextWord = wordsList[nextIndex];
                const nextQuestionType = Math.random() > 0.5 ? "vowel" : "consonant";
                const { vowelCount, consonantCount } = getVowelConsonantCount(nextWord);
                const correct = nextQuestionType === "vowel" ? vowelCount : consonantCount;

                setCurrentIndex(nextIndex);
                setQuestionType(nextQuestionType);
                setOptions(generateOptions(correct));
                setMessage("");
                setShowImage(false);
            }, 3000);
        } else {
            setMessage("❌ Incorrect, try again!");
        }
    };

    // On first load
    useEffect(() => {
        shuffleArray(wordsList);
        const firstWord = wordsList[0];
        const { vowelCount, consonantCount } = getVowelConsonantCount(firstWord);
        const correct = questionType === "vowel" ? vowelCount : consonantCount;
        setOptions(generateOptions(correct));
        document.title = "Vowels and Consonants Worksheets for UKG | Learn A, E, I, O, U";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Introduce LKG kids to vowels and consonants with fun and interactive activities at Mama’s Guidance. Build early reading skills through engaging exercises and colorful visuals." />
            </Helmet>

            {showImage === false ? (
                <div className="contentPart showCenter fillPreposition additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Vowels and Consonants</h1>

                            <p>
                                Build a strong foundation in English with this engaging <strong>Vowels and Consonants</strong> worksheet for UKG students. Children will learn to recognize the <strong>5 vowels</strong> – A, E, I, O, U – and differentiate them from the other <strong>21 consonants</strong> in the alphabet.
                            </p>

                            <h2>🔤 What Are Vowels and Consonants?</h2>
                            <p>
                                In the English alphabet, there are 26 letters. Out of these, <strong>5 are vowels</strong> (A, E, I, O, U) and the remaining <strong>21 are consonants</strong>. Vowels help form words and are used in almost every sentence. Recognizing them is the first step toward learning to read and spell.
                            </p>

                            <h2>🧩 What’s in the Worksheet?</h2>
                            <ul>
                                <li>Circle the vowels from a group of letters</li>
                                <li>Match vowels and consonants with objects or pictures</li>
                                <li>Color the vowels in red and consonants in blue</li>
                                <li>Simple sorting and identification exercises</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify vowel letters in the alphabet</li>
                                <li>Differentiate between vowels and consonants</li>
                                <li>Improve letter recognition and phonics</li>
                                <li>Prepare for reading CVC (consonant-vowel-consonant) words</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Say each letter aloud with your child.</li>
                                <li>Ask them to circle or color the vowels as instructed.</li>
                                <li>Use real-life examples of objects that start with vowels and consonants.</li>
                                <li>Play phonics games to reinforce sound recognition.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Important</h2>
                            <p>
                                Recognizing vowels and consonants is the base of phonics, spelling, and word construction. It helps children <strong>understand letter sounds, word patterns, and pronunciation</strong> – essential skills for early readers.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Vowels and Consonants</h1>

                        <p>
                            Build a strong foundation in English with this engaging <strong>Vowels and Consonants</strong> worksheet for LKG students. Children will learn to recognize the <strong>5 vowels</strong> – A, E, I, O, U – and differentiate them from the other <strong>21 consonants</strong> in the alphabet.
                        </p>

                        <h2>🔤 What Are Vowels and Consonants?</h2>
                        <p>
                            In the English alphabet, there are 26 letters. Out of these, <strong>5 are vowels</strong> (A, E, I, O, U) and the remaining <strong>21 are consonants</strong>. Vowels help form words and are used in almost every sentence. Recognizing them is the first step toward learning to read and spell.
                        </p>

                        <h2>🧩 What’s in the Worksheet?</h2>
                        <ul>
                            <li>Circle the vowels from a group of letters</li>
                            <li>Match vowels and consonants with objects or pictures</li>
                            <li>Color the vowels in red and consonants in blue</li>
                            <li>Simple sorting and identification exercises</li>
                        </ul>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify vowel letters in the alphabet</li>
                            <li>Differentiate between vowels and consonants</li>
                            <li>Improve letter recognition and phonics</li>
                            <li>Prepare for reading CVC (consonant-vowel-consonant) words</li>
                        </ul>

                        <h2>👩‍🏫 How to Use the Worksheet</h2>
                        <ul>
                            <li>Say each letter aloud with your child.</li>
                            <li>Ask them to circle or color the vowels as instructed.</li>
                            <li>Use real-life examples of objects that start with vowels and consonants.</li>
                            <li>Play phonics games to reinforce sound recognition.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/english-language/this-that-these-those"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>This–That & These–Those</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/english-language/rhyming-words"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Rhyming Words</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/english-language/opposites"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Opposites</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Is Important</h2>
                        <p>
                            Recognizing vowels and consonants is the base of phonics, spelling, and word construction. It helps children <strong>understand letter sounds, word patterns, and pronunciation</strong> – essential skills for early readers.
                        </p>
                    </article>

                    <h2>Word: <strong>{wordsList[currentIndex]}</strong></h2>
                    <p>How many <strong>{questionType}s</strong> are in the word?</p>

                    <div className="optionContainer" style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
                        {options.map((option, idx) => (
                            <div
                                key={idx}
                                onClick={() => handleOptionClick(option)}
                                className="background-blue"
                                style={{ minWidth: "80px", fontSize: "18px" }}
                            >
                                {option}
                            </div>
                        ))}
                    </div>

                    <p style={{ marginTop: "10px", fontWeight: "bold", color: message.startsWith("✅") ? "green" : "red" }}>
                        {message}
                    </p>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well Done"
                    />
                </div>
            )}
        </>
    );
};

export default VowelConsonant;
